import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  Grid2 as Grid,
  Divider,
  Chip,
  Paper,
  FormGroup,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import TimeField from './ui/TimeField';
import { SHIFT_DEFAULTS } from '../utils/constants';

// Formata TimeSpan do C# (HH:mm:ss) para input time (HH:mm)
const formatTimeSpan = (timeSpan) => {
  if (!timeSpan) return '09:00';
  if (typeof timeSpan === 'string') return timeSpan.substring(0, 5);
  return '09:00';
};

/**
 * ShiftForm — criação/edição de turno.
 *
 * A lista de serviços mostra TODOS os serviços do usuário (não só os já vinculados a
 * este local/profissional), cada um com seus vínculos (📍 locais, 👤 profissionais).
 * Ao marcar um serviço ainda NÃO vinculado a este profissional neste local, avisamos que,
 * ao salvar, o vínculo (place_service + place_service_professional) será criado "pela culatra"
 * — a orquestração acontece no container (SchedulesPage.handleShiftSubmit) antes de gravar o turno.
 */
export default function ShiftForm({
  open,
  onClose,
  onSubmit,
  onDelete,
  shift,
  scheduleId,
  context = {},
  services = [],
  placeServices = [],
  placeServiceProfessionals = [],
  places = [],
  professionals = [],
}) {
  const [formData, setFormData] = useState({
    scheduleId: scheduleId || '',
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
    startTime: SHIFT_DEFAULTS.START_TIME,
    endTime: SHIFT_DEFAULTS.END_TIME,
    breakBetweenAppointmentsMinutes: SHIFT_DEFAULTS.BREAK_BETWEEN_APPOINTMENTS,
    isActive: true,
    serviceIds: [], // Backend espera serviceIds (ServiceId base), não placeServiceIds
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (shift) {
      setFormData({
        scheduleId: shift.scheduleId || scheduleId,
        monday: shift.monday || false,
        tuesday: shift.tuesday || false,
        wednesday: shift.wednesday || false,
        thursday: shift.thursday || false,
        friday: shift.friday || false,
        saturday: shift.saturday || false,
        sunday: shift.sunday || false,
        startTime: shift.startTime ? formatTimeSpan(shift.startTime) : '09:00',
        endTime: shift.endTime ? formatTimeSpan(shift.endTime) : '18:00',
        breakBetweenAppointmentsMinutes: shift.breakBetweenAppointmentsMinutes || 0,
        isActive: shift.isActive ?? true,
        // Backend retorna shift.services com serviceId
        serviceIds: shift.services?.map((s) => s.serviceId) || [],
      });
    } else {
      setFormData({
        scheduleId: scheduleId || '',
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
        startTime: SHIFT_DEFAULTS.START_TIME,
        endTime: SHIFT_DEFAULTS.END_TIME,
        breakBetweenAppointmentsMinutes: SHIFT_DEFAULTS.BREAK_BETWEEN_APPOINTMENTS,
        isActive: true,
        serviceIds: [],
      });
    }
    setErrors({});
  }, [shift, scheduleId, open]);

  // VM: todos os serviços do usuário + seus vínculos (local/profissional) + se já está
  // vinculado a ESTE profissional neste local.
  const servicesVm = useMemo(() => {
    const placeNameById = new Map((places || []).map((p) => [p.id, p.name]));
    const profNameById = new Map((professionals || []).map((p) => [p.id, p.name]));
    const placeProfessionalId = context?.placeProfessionalId;

    return (services || [])
      .map((svc) => {
        const serviceId = svc.id;
        const name = svc.name || svc.serviceName || 'Serviço';
        const duration = svc.defaultDurationMinutes ?? svc.durationMinutes ?? 30;
        const price = svc.defaultPrice ?? svc.price ?? null;

        const placeIds = [
          ...new Set((placeServices || []).filter((ps) => ps.serviceId === serviceId).map((ps) => ps.placeId)),
        ];
        const placeLabels = placeIds.map((id) => placeNameById.get(id)).filter(Boolean);

        const profIds = [
          ...new Set(
            (placeServiceProfessionals || [])
              .filter((psp) => psp.serviceId === serviceId)
              .map((psp) => psp.professionalId),
          ),
        ];
        const profLabels = profIds.map((id) => profNameById.get(id)).filter(Boolean);

        const boundHere = (placeServiceProfessionals || []).some(
          (psp) => psp.placeProfessionalId === placeProfessionalId && psp.serviceId === serviceId,
        );

        return { serviceId, name, duration, price, placeLabels, profLabels, boundHere };
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [services, placeServices, placeServiceProfessionals, places, professionals, context]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    if (errors[name]) setErrors({ ...errors, [name]: null });
  };

  const handleServiceToggle = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      serviceIds: prev.serviceIds.includes(serviceId)
        ? prev.serviceIds.filter((id) => id !== serviceId)
        : [...prev.serviceIds, serviceId],
    }));
    if (errors.services) setErrors({ ...errors, services: null });
  };

  const validateForm = () => {
    const newErrors = {};

    const hasAtLeastOneDay =
      formData.monday || formData.tuesday || formData.wednesday || formData.thursday || formData.friday || formData.saturday || formData.sunday;
    if (!hasAtLeastOneDay) newErrors.weekdays = 'Selecione pelo menos um dia da semana';

    if (formData.startTime >= formData.endTime) newErrors.endTime = 'Horário de fim deve ser posterior ao horário de início';

    if (formData.breakBetweenAppointmentsMinutes < 0) newErrors.breakBetweenAppointmentsMinutes = 'Intervalo não pode ser negativo';

    if (formData.serviceIds.length === 0) {
      newErrors.services = 'Selecione pelo menos um serviço para este turno — sem serviço a agenda não gera horários.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    const dataToSubmit = {
      ...formData,
      startTime: `${formData.startTime}:00`,
      endTime: `${formData.endTime}:00`,
      breakBetweenAppointmentsMinutes: parseInt(formData.breakBetweenAppointmentsMinutes),
    };
    onSubmit(dataToSubmit);
  };

  const handleDelete = () => {
    if (!shift?.id) return; // turno não persistido — não há o que excluir
    if (window.confirm('Tem certeza que deseja excluir este turno?')) onDelete(shift.id);
  };

  const getSelectedDays = () => {
    const days = [];
    if (formData.monday) days.push('Seg');
    if (formData.tuesday) days.push('Ter');
    if (formData.wednesday) days.push('Qua');
    if (formData.thursday) days.push('Qui');
    if (formData.friday) days.push('Sex');
    if (formData.saturday) days.push('Sáb');
    if (formData.sunday) days.push('Dom');
    return days.join(', ');
  };

  const selectedServices = servicesVm.filter((s) => formData.serviceIds.includes(s.serviceId));
  const selectedServiceNames = selectedServices.map((s) => s.name).join(', ');
  const minDuration = selectedServices.length > 0 ? Math.min(...selectedServices.map((s) => s.duration || 30)) : null;

  // Renderiza chips de vínculo (local/profissional) com corte em 3 + "+N"
  const renderBindingChips = (labels, iconEl) => {
    const shown = labels.slice(0, 3);
    const extra = labels.length - shown.length;
    return (
      <>
        {shown.map((label, i) => (
          <Chip
            key={`${label}-${i}`}
            size="small"
            variant="outlined"
            icon={iconEl}
            label={label}
            sx={{ height: 22 }}
          />
        ))}
        {extra > 0 && <Chip size="small" variant="outlined" label={`+${extra}`} sx={{ height: 22 }} />}
      </>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{shift?.id ? 'Editar Turno' : 'Novo Turno'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
          {/* Dias da semana */}
          <Box>
            <Typography variant="subtitle2" gutterBottom fontWeight="medium">
              Dias da Semana *
            </Typography>
            <FormGroup row>
              {[
                ['monday', 'Segunda'],
                ['tuesday', 'Terça'],
                ['wednesday', 'Quarta'],
                ['thursday', 'Quinta'],
                ['friday', 'Sexta'],
                ['saturday', 'Sábado'],
                ['sunday', 'Domingo'],
              ].map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  control={<Checkbox name={key} checked={formData[key]} onChange={handleChange} />}
                  label={label}
                />
              ))}
            </FormGroup>
            {errors.weekdays && (
              <Typography variant="caption" color="error">
                {errors.weekdays}
              </Typography>
            )}
          </Box>

          {/* Horários */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TimeField
                label="Horário de Início"
                value={formData.startTime}
                onChange={(newTime) => {
                  setFormData({ ...formData, startTime: newTime });
                  if (errors.startTime) setErrors({ ...errors, startTime: null });
                }}
                error={!!errors.startTime}
                helperText={errors.startTime || 'Formato 24h (00:00 - 23:59)'}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TimeField
                label="Horário de Fim"
                value={formData.endTime}
                onChange={(newTime) => {
                  setFormData({ ...formData, endTime: newTime });
                  if (errors.endTime) setErrors({ ...errors, endTime: null });
                }}
                error={!!errors.endTime}
                helperText={errors.endTime || 'Formato 24h (00:00 - 23:59)'}
                required
              />
            </Grid>
          </Grid>

          {/* Intervalo */}
          <TextField
            fullWidth
            label="Intervalo entre Consultas (minutos)"
            name="breakBetweenAppointmentsMinutes"
            type="number"
            value={formData.breakBetweenAppointmentsMinutes}
            onChange={handleChange}
            inputProps={{ min: 0, step: 5 }}
            error={!!errors.breakBetweenAppointmentsMinutes}
            helperText={errors.breakBetweenAppointmentsMinutes || 'Tempo de descanso entre um atendimento e outro (ex: 0, 10, 15 minutos)'}
          />

          <Divider />

          {/* Serviços atendidos neste turno */}
          <Box>
            <Typography variant="subtitle2" gutterBottom fontWeight="medium">
              Serviços atendidos neste turno *
            </Typography>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Marque quais serviços você atende neste turno. Os horários são gerados com base no menor serviço selecionado.
              Você pode marcar um serviço ainda não oferecido aqui — ao salvar, ele será vinculado a este local e profissional.
            </Typography>

            {servicesVm.length === 0 ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Você ainda não cadastrou nenhum serviço. Cadastre um serviço primeiro na aba de Serviços.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                {servicesVm.map((svc) => {
                  const checked = formData.serviceIds.includes(svc.serviceId);
                  const willBind = checked && !svc.boundHere;
                  return (
                    <Paper
                      key={svc.serviceId}
                      variant="outlined"
                      sx={{ p: 1.5, borderColor: willBind ? 'warning.main' : undefined }}
                    >
                      <FormControlLabel
                        sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={() => handleServiceToggle(svc.serviceId)}
                            sx={{ pt: 0 }}
                            aria-label={`Selecionar serviço ${svc.name}`}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pt: 0.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography fontWeight="medium">{svc.name}</Typography>
                              <Chip label={`${svc.duration} min`} size="small" variant="outlined" sx={{ height: 20 }} />
                              {svc.price != null && (
                                <Chip label={`R$ ${Number(svc.price).toFixed(2)}`} size="small" color="primary" variant="outlined" sx={{ height: 20 }} />
                              )}
                              {svc.boundHere && (
                                <Chip label="Oferecido aqui" size="small" color="success" variant="outlined" sx={{ height: 20 }} />
                              )}
                            </Box>

                            {(svc.placeLabels.length > 0 || svc.profLabels.length > 0) && (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                {renderBindingChips(svc.placeLabels, <LocationOnIcon sx={{ fontSize: '0.85rem !important' }} />)}
                                {renderBindingChips(svc.profLabels, <PersonIcon sx={{ fontSize: '0.85rem !important' }} />)}
                              </Box>
                            )}

                            {willBind && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'warning.dark' }}>
                                <WarningAmberIcon sx={{ fontSize: '1rem' }} />
                                <Typography variant="caption">
                                  Ao salvar, este serviço será vinculado a {context?.placeName || 'este local'} e a este profissional.
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </Paper>
                  );
                })}
              </Box>
            )}
            {errors.services && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                {errors.services}
              </Typography>
            )}
          </Box>

          {/* Status ativo */}
          {shift && (
            <FormControlLabel
              control={<Checkbox name="isActive" checked={formData.isActive} onChange={handleChange} />}
              label="Turno ativo"
            />
          )}

          {/* Resumo */}
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Resumo:</strong>
              <br />
              <strong>Dias:</strong> {getSelectedDays() || 'Nenhum dia selecionado'}
              <br />
              <strong>Horário:</strong> {formData.startTime} às {formData.endTime}
              <br />
              <strong>Serviços:</strong> {selectedServiceNames || 'Nenhum serviço selecionado'}
              {minDuration != null && (
                <>
                  <br />
                  <strong>Duração dos slots:</strong> {minDuration} minutos (baseado no menor serviço)
                </>
              )}
              {formData.breakBetweenAppointmentsMinutes > 0 && (
                <>
                  <br />
                  <strong>Intervalo:</strong> {formData.breakBetweenAppointmentsMinutes} minutos entre consultas
                </>
              )}
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Box>
          {shift?.id && onDelete && (
            <Button startIcon={<DeleteIcon />} onClick={handleDelete} color="error" variant="outlined">
              Excluir
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {shift ? 'Salvar' : 'Criar'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

ShiftForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  shift: PropTypes.object,
  scheduleId: PropTypes.string.isRequired,
  context: PropTypes.shape({
    placeId: PropTypes.string,
    professionalId: PropTypes.string,
    placeProfessionalId: PropTypes.string,
    placeName: PropTypes.string,
  }),
  services: PropTypes.arrayOf(PropTypes.object),
  placeServices: PropTypes.arrayOf(PropTypes.object),
  placeServiceProfessionals: PropTypes.arrayOf(PropTypes.object),
  places: PropTypes.arrayOf(PropTypes.object),
  professionals: PropTypes.arrayOf(PropTypes.object),
};
